import type { DataSourceAbstract, DTSelect, DTSelectItem, RelativeSelectConfig, SelectField, SingleSelectField } from '@lighthouse/core'
import { max } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAppThemeContext } from '../../../ApplicationContainer'
import { applyFillPickerValue4CssColorValue } from '../../../FillPicker'
import { outlineWidth } from '../../constant'
import type { FieldBaseProps } from '../../types'
import { CheckBoxGroup } from './CheckBoxGroup'
import MultiSelect from './MultiSelect'
import * as SC from './styles'
import { TagGroup } from './TagGroup'

interface SelectFieldInputProps extends FieldBaseProps {
    value: string[]
    selectConfig?: DTSelect
    relativeSelectConfig: RelativeSelectConfig
}

const SelectFieldInput: React.FC<SelectFieldInputProps> = props => {
    const {
        onChange,
        value: data,
        isControlled,
        readOnly,
        dataSource,
        relativeSelectConfig,
        selectConfig,
        config,
        onFetchDataSourceMeta,
        onRenderLabel
    } = props
    const { theme } = useAppThemeContext()
    const { id: pointer } = dataSource ?? {}
    const { fieldPointer = '', placeholder, relativeSelect } = relativeSelectConfig
    const { showMode, direction, align } = relativeSelect ?? {}
    const { options: selectOptions = [], multiple: canMultipleChoice } = selectConfig ?? {}
    const { breakPoint } = config
    const containerRef = useRef<HTMLDivElement>(null)
    const [value, setValue] = useState(data ?? [])
    const [newDataSource, setNewDataSource] = useState<DataSourceAbstract>()
    const [opened, setOpened] = useState(false)
    const { placeholderColor, fontFamily } = useMemo(
        () => ({
            placeholderColor: applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes),
            fontFamily: breakPoint?.font?.fontFamily
        }),
        [breakPoint?.design?.placeholderColor?.color, breakPoint?.font?.fontFamily, theme.palettes]
    )
    const usedValue = useMemo(() => (isControlled ? data : value), [data, isControlled, value])

    const options = useMemo(() => {
        if (newDataSource) {
            const field = newDataSource.schema?.[fieldPointer]
            const options: DTSelectItem[] =
                field.type === 'singleSelect' ? (field as SingleSelectField).singleSelect.options : (field as SelectField).select.options
            return options.map(({ label, color }) => {
                return {
                    label,
                    value: label,
                    color,
                    isDelete: !readOnly
                }
            })
        }
        return selectOptions.map(({ label, color }) => ({
            label,
            value: label,
            color,
            isDelete: !readOnly
        }))
    }, [fieldPointer, newDataSource, readOnly, selectOptions])

    const inputWidth = containerRef.current?.getBoundingClientRect()?.width ?? 380

    const popoverWidth = useMemo(() => max(inputWidth, 380) + outlineWidth * 2, [inputWidth])

    const handleMultiChange = useCallback(
        (value: string[]) => {
            setValue(value)
            onChange?.({ type: 'relativeSelect', value })
        },
        [onChange]
    )

    useEffect(() => {
        if (pointer && opened) {
            onFetchDataSourceMeta?.({ dsId: pointer }).then(v => {
                setNewDataSource(dataSource)
            })
        }
    }, [dataSource, onFetchDataSourceMeta, opened, pointer])

    return useMemo(() => {
        switch (showMode) {
            case 'checkbox': {
                return (
                    <SC.Container>
                        <CheckBoxGroup
                            breakPoint={breakPoint}
                            options={options}
                            isMultiple={canMultipleChoice}
                            direction={direction}
                            align={align}
                            value={usedValue}
                            disabled={readOnly}
                            onRenderLabel={onRenderLabel}
                            onChange={handleMultiChange}
                        />
                    </SC.Container>
                )
            }
            case 'tag': {
                return (
                    <SC.Container>
                        <TagGroup
                            options={options}
                            isMultiple={canMultipleChoice}
                            direction={direction}
                            align={align}
                            value={usedValue}
                            disabled={readOnly}
                            onChange={handleMultiChange}
                        />
                    </SC.Container>
                )
            }
            default: {
                return (
                    <SC.Container data-field-input data-field-active={opened} data-field-disabled={!!readOnly} ref={containerRef}>
                        <MultiSelect
                            dropDownProps={{
                                styles: {
                                    dropdown: {
                                        fontFamily
                                    }
                                }
                            }}
                            options={options}
                            opened={opened}
                            width={popoverWidth}
                            value={usedValue}
                            placeholder={placeholder}
                            placeholderColor={placeholderColor}
                            readOnly={readOnly}
                            isMultiple={canMultipleChoice}
                            onChange={handleMultiChange}
                            onChangePopover={setOpened}
                        />
                    </SC.Container>
                )
            }
        }
    }, [
        align,
        breakPoint,
        canMultipleChoice,
        direction,
        fontFamily,
        handleMultiChange,
        onRenderLabel,
        opened,
        options,
        placeholder,
        placeholderColor,
        popoverWidth,
        readOnly,
        showMode,
        usedValue
    ])
}

export default SelectFieldInput
